import { useEffect, useState } from "react";

import { X } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { Label } from "~/components/ui/label";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { Textarea } from "~/components/ui/textarea";
import { cn } from "~/lib/utils";
import type { SurveyDeliveryPrompt, SurveyQuestionDisplay } from "../types";

interface EmbeddedSurveyPromptProps {
    prompt: SurveyDeliveryPrompt;
    onAnswer: (
        answer: string | string[],
        question: SurveyQuestionDisplay,
        deliveryId: string,
    ) => void;
    onDismiss?: () => void;
    // AIDEV-NOTE: Fires when the user finishes the final question (drip or single).
    // Parents use this to drive post-survey UI (e.g. "thanks" + auto-close).
    onComplete?: () => void;
    isSubmitting?: boolean;
    title?: string;
}

export function EmbeddedSurveyPrompt({
    prompt,
    onAnswer,
    onDismiss,
    onComplete,
    isSubmitting = false,
    title = "Quick question",
}: EmbeddedSurveyPromptProps) {
    const questions =
        prompt.survey.questions?.length > 0
            ? prompt.survey.questions
            : [prompt.question];
    const [question, setQuestion] = useState(prompt.question);
    const [isLocallyComplete, setIsLocallyComplete] = useState(false);
    const [selectedValue, setSelectedValue] = useState("");
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [textValue, setTextValue] = useState("");

    useEffect(() => {
        setQuestion(prompt.question);
        setIsLocallyComplete(false);
        setSelectedValue("");
        setSelectedValues([]);
        setTextValue("");
    }, [prompt.deliveryId, prompt.question.id]);

    const isValid =
        question.questionType === "multiple_choice"
            ? !!selectedValue
            : question.questionType === "multiple_select"
              ? selectedValues.length > 0
              : !!textValue.trim();

    const resetInputs = () => {
        setSelectedValue("");
        setSelectedValues([]);
        setTextValue("");
    };

    const submitAnswer = (answer: string | string[]) => {
        const deliveryId =
            prompt.questionDeliveryIds?.[question.id] ?? prompt.deliveryId;
        const questionIndex = questions.findIndex(
            (candidate) => candidate.id === question.id,
        );
        const nextQuestion =
            prompt.deliveryMode === "drip" && questionIndex >= 0
                ? questions[questionIndex + 1]
                : undefined;

        if (nextQuestion) {
            setQuestion(nextQuestion);
            resetInputs();
        } else {
            setIsLocallyComplete(true);
            onComplete?.();
        }

        onAnswer(answer, question, deliveryId);
    };

    const handleSubmit = () => {
        if (question.questionType === "multiple_choice" && selectedValue) {
            submitAnswer(selectedValue);
        } else if (
            question.questionType === "multiple_select" &&
            selectedValues.length > 0
        ) {
            submitAnswer(selectedValues);
        } else if (
            (question.questionType === "short_text" ||
                question.questionType === "long_text") &&
            textValue.trim()
        ) {
            submitAnswer(textValue.trim());
        }
    };

    const toggleMultiSelect = (value: string, checked: boolean) => {
        setSelectedValues((current) =>
            checked
                ? [...current, value]
                : current.filter((selected) => selected !== value),
        );
    };

    if (isLocallyComplete) {
        return null;
    }

    return (
        <section
            data-testid="survey-embedded-prompt"
            aria-busy={isSubmitting || undefined}
            className="rounded-lg border border-brand-forest-200 bg-white p-4 shadow-sm"
        >
            <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                    <p className="text-xs font-medium uppercase text-brand-forest-500">
                        {title}
                    </p>
                    <h3 className="mt-1 text-base font-semibold text-brand-forest-900">
                        {question.questionText}
                    </h3>
                    {question.descriptionText && (
                        <p className="mt-1 text-sm text-muted-foreground">
                            {question.descriptionText}
                        </p>
                    )}
                </div>
                {onDismiss && prompt.dismissalBehavior !== "none" && (
                    <button
                        type="button"
                        onClick={onDismiss}
                        className="rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                        aria-label="Dismiss"
                    >
                        <X className="size-4" />
                    </button>
                )}
            </div>

            {question.imageSignedUrl && (
                <img
                    src={question.imageSignedUrl}
                    alt=""
                    className="mb-4 aspect-[3/2] w-full rounded-md object-cover"
                />
            )}

            {question.questionType === "multiple_choice" &&
                question.options && (
                    <RadioGroup
                        value={selectedValue}
                        onValueChange={setSelectedValue}
                        className="space-y-2"
                    >
                        {question.options.map((option) => (
                            <div
                                key={option.value}
                                onClick={() => setSelectedValue(option.value)}
                                className={cn(
                                    "flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2",
                                    selectedValue === option.value
                                        ? "border-brand-forest-500 bg-brand-forest-50"
                                        : "border-brand-cream-300",
                                )}
                            >
                                <RadioGroupItem
                                    value={option.value}
                                    id={`${prompt.deliveryId}-${option.value}`}
                                />
                                <Label
                                    htmlFor={`${prompt.deliveryId}-${option.value}`}
                                    className="flex-1 cursor-pointer text-sm"
                                >
                                    {option.label}
                                </Label>
                            </div>
                        ))}
                    </RadioGroup>
                )}

            {question.questionType === "multiple_select" &&
                question.options && (
                    <div className="space-y-2">
                        {question.options.map((option) => (
                            <div
                                key={option.value}
                                className={cn(
                                    "flex items-center gap-3 rounded-md border px-3 py-2",
                                    selectedValues.includes(option.value)
                                        ? "border-brand-forest-500 bg-brand-forest-50"
                                        : "border-brand-cream-300",
                                )}
                            >
                                <Checkbox
                                    id={`${prompt.deliveryId}-${option.value}`}
                                    checked={selectedValues.includes(
                                        option.value,
                                    )}
                                    onCheckedChange={(checked) =>
                                        toggleMultiSelect(
                                            option.value,
                                            checked === true,
                                        )
                                    }
                                />
                                <Label
                                    htmlFor={`${prompt.deliveryId}-${option.value}`}
                                    className="flex-1 cursor-pointer text-sm"
                                >
                                    {option.label}
                                </Label>
                            </div>
                        ))}
                    </div>
                )}

            {(question.questionType === "short_text" ||
                question.questionType === "long_text") && (
                <Textarea
                    value={textValue}
                    onChange={(event) => setTextValue(event.target.value)}
                    placeholder="Type your answer..."
                    rows={question.questionType === "long_text" ? 4 : 2}
                    className="resize-none"
                />
            )}

            <Button
                type="button"
                className="mt-4 w-full"
                onClick={handleSubmit}
                disabled={!isValid}
            >
                Submit
            </Button>
        </section>
    );
}
